'use client'

import { useCallback, useEffect, useState } from 'react'
import {
  LayoutDashboard,
  User,
  History,
  BarChart3,
  FolderOpen,
  Loader2,
  AlertCircle,
} from 'lucide-react'
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '@/components/ui/sheet'
import { Button } from '@/components/ui/button'
import { DashboardData, DashboardPayment, DashboardTab } from './types'
import OverviewSection from './sections/OverviewSection'
import ProfileSection from './sections/ProfileSection'
import HistorySection from './sections/HistorySection'
import StatsSection from './sections/StatsSection'
import DocumentsSection from './sections/DocumentsSection'

interface Props {
  open: boolean
  onOpenChange: (open: boolean) => void
  initialTab?: DashboardTab
  /** Déclenché par les boutons « Recharger » (ouvre la tarification). */
  onRecharge: () => void
  /** Permet de synchroniser le solde affiché dans l'en-tête de la page. */
  onCreditsLoaded?: (credits: number) => void
}

const TABS: { key: DashboardTab; label: string; icon: React.ComponentType<{ className?: string }> }[] = [
  { key: 'overview', label: "Vue d'ensemble", icon: LayoutDashboard },
  { key: 'profil', label: 'Profil', icon: User },
  { key: 'history', label: 'Historique', icon: History },
  { key: 'stats', label: 'Statistiques', icon: BarChart3 },
  { key: 'documents', label: 'Documents', icon: FolderOpen },
]

export default function DashboardSheet({
  open,
  onOpenChange,
  initialTab = 'overview',
  onRecharge,
  onCreditsLoaded,
}: Props) {
  const [activeTab, setActiveTab] = useState<DashboardTab>(initialTab)
  const [data, setData] = useState<DashboardData | null>(null)
  // Les paiements vivent dans un état SÉPARÉ de `data`, servis par une autre
  // route et portant des montants en ARGENT (cf. `DashboardPayment`). Les fondre
  // dans `DashboardData` rouvrirait la confusion crédits/argent qui avait rendu
  // l'historique des paiements inopérant.
  const [payments, setPayments] = useState<DashboardPayment[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(false)

  const fetchData = useCallback(async () => {
    setLoading(true)
    setError(false)
    try {
      const res = await fetch('/api/dashboard')
      if (!res.ok) {
        console.error('[dashboard] /api/dashboard a échoué:', res.status)
        setError(true)
        return
      }
      const d = (await res.json()) as DashboardData
      setData(d)
      onCreditsLoaded?.(d.profile.credits)
    } catch (e) {
      console.error('[dashboard] Erreur de chargement:', e)
      setError(true)
    } finally {
      setLoading(false)
    }
  }, [onCreditsLoaded])

  /**
   * Historique des paiements — chargé À PART, et volontairement NON bloquant.
   *
   * BEST-EFFORT assumé (D-023) : l'échec est acceptable — seul le filtre
   * « Paiements » se retrouve vide, les quatre autres onglets restent
   * pleinement fonctionnels ; aucune incohérence métier, cette lecture ne
   * conditionne ni crédits ni droits ; et faire échouer tout le tableau de bord
   * parce qu'une seule de ses sources est indisponible serait disproportionné.
   * L'erreur est journalisée, jamais avalée en silence.
   */
  const fetchPayments = useCallback(async () => {
    try {
      const res = await fetch('/api/payment/history')
      if (!res.ok) {
        console.error('[dashboard] /api/payment/history a échoué:', res.status)
        setPayments([])
        return
      }
      const d = (await res.json()) as { payments?: DashboardPayment[] }
      setPayments(d.payments ?? [])
    } catch (e) {
      console.error('[dashboard] Erreur de chargement des paiements:', e)
      setPayments([])
    }
  }, [])

  // À chaque ouverture : positionner l'onglet cible + (re)charger les données.
  useEffect(() => {
    if (open) {
      setActiveTab(initialTab)
      fetchData()
      fetchPayments()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  // Changement d'onglet cible alors que le panneau est déjà ouvert.
  useEffect(() => {
    if (open) setActiveTab(initialTab)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialTab])

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-xl overflow-y-auto p-0 flex flex-col">
        {/* En-tête + navigation */}
        <div className="border-b border-tq-border bg-white sticky top-0 z-10">
          <SheetHeader className="px-6 pt-6 pb-4">
            <SheetTitle className="flex items-center gap-2 text-tq-text">
              <LayoutDashboard className="h-5 w-5 text-tq-green" />
              Mon tableau de bord
            </SheetTitle>
            <SheetDescription>Compte, activité, historique et documents.</SheetDescription>
          </SheetHeader>

          <div className="px-6 pb-3">
            <div className="flex gap-1 bg-tq-bg rounded-lg p-1 overflow-x-auto">
              {TABS.map(({ key, label, icon: Icon }) => (
                <button
                  key={key}
                  onClick={() => setActiveTab(key)}
                  className={`flex-1 whitespace-nowrap flex items-center justify-center gap-1.5 px-3 py-2 rounded-md text-xs font-medium transition-all ${
                    activeTab === key
                      ? 'bg-white text-tq-text shadow-sm'
                      : 'text-tq-muted hover:text-tq-text'
                  }`}
                >
                  <Icon className="h-3.5 w-3.5" />
                  <span className="hidden sm:inline">{label}</span>
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Contenu */}
        <div className="px-6 py-5 flex-1">
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="h-8 w-8 animate-spin text-tq-green" />
            </div>
          ) : error ? (
            <div className="text-center py-16">
              <AlertCircle className="h-10 w-10 text-red-400 mx-auto mb-3" />
              <p className="text-sm text-tq-text font-medium">Impossible de charger le tableau de bord.</p>
              <p className="text-xs text-tq-muted mt-1">Vérifiez votre connexion puis réessayez.</p>
              <Button variant="outline" size="sm" onClick={() => { fetchData(); fetchPayments() }} className="mt-4">
                Réessayer
              </Button>
            </div>
          ) : data ? (
            <>
              {activeTab === 'overview' && (
                <OverviewSection data={data} onGoToTab={setActiveTab} onRecharge={onRecharge} />
              )}
              {activeTab === 'profil' && <ProfileSection data={data} onRecharge={onRecharge} />}
              {activeTab === 'history' && <HistorySection data={data} payments={payments} />}
              {activeTab === 'stats' && <StatsSection data={data} />}
              {activeTab === 'documents' && <DocumentsSection data={data} />}
            </>
          ) : null}
        </div>

        {/* Pied */}
        <div className="border-t border-tq-border px-6 py-4 mt-auto bg-white">
          <div className="flex justify-between items-center">
            <Button variant="outline" size="sm" onClick={() => { fetchData(); fetchPayments() }} disabled={loading} className="gap-1.5">
              {loading && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
              Rafraîchir
            </Button>
            <Button variant="ghost" size="sm" onClick={() => onOpenChange(false)} className="text-tq-muted">
              Fermer
            </Button>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  )
}
